import React, { useCallback, useEffect, useMemo } from 'react';
import { useField } from 'react-final-form';
import { observer } from 'mobx-react';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { createMask } from 'ksnm-common-ui/lib/MaskedInput';
import { Tooltip } from 'ksnm-common-ui/lib/Tooltip';

import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { indicatorEditStore } from '@stores/dictionaryStores/indicatorsStore/indicatorEditStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';
import { roundTypeStore } from '@stores/dictionaryStores/roundTypeStore';
import type { ReportGroupsDto } from '@src/api';

import styles from '../IndicatorEdit.module.scss';

const renderName = (value: ReportGroupsDto) => `${value.code} - ${value.name}`;

const packageSourceFieldItem = [
  {
    id: 'org_code',
    name: 'org_code - Код организации',
  },
  {
    id: 'rep_group_code',
    name: 'rep_group_code - Код группы отчетности',
  },
  {
    id: 'year',
    name: 'year - Отчетный год',
  },
  {
    id: 'period',
    name: 'period - Отчетный период',
  },
  {
    id: 'version',
    name: 'version - Версия отчетности',
  },
];

export const Form = observer(() => {
  const { isEditMode, isNew, getTableFieldsInfo, sourceFields, groupingColumns, aggFuncs, varSources } =
    indicatorEditStore;
  const { reportGroups } = reportGroupsStore;
  const { reportVersions } = reportVersionsStore;
  const { roundTypes } = roundTypeStore;

  useEffect(() => {
    fetchReferences(['reportGroups', 'reportVersions', 'indicators', 'filterOperators', 'roundTypes']);
  }, []);

  const {
    input: { value: reportGroupCode, onChange: changeReportGroupCode },
  } = useField('reportGroupCode');

  const {
    input: { onChange: changeRepVers, value: repVers },
  } = useField('repVers');

  const {
    input: { value: varSource },
  } = useField('varSource');

  const {
    input: { onChange: changeSourceField, value: sourceField },
  } = useField('sourceField');

  const {
    input: { onChange: changeGroupBy },
  } = useField('groupBy');

  const {
    input: { onChange: changeAggFunction },
  } = useField('aggFunction');

  const {
    input: { value: filters, onChange: changeFilters },
  } = useField('filters');

  const {
    input: { value: roundingType, onChange: changeRoundingType },
  } = useField('roundingType');

  const {
    input: { onChange: changeDecimalQty },
  } = useField('decimalQty');

  useEffect(() => {
    if (repVers) getTableFieldsInfo(repVers);
  }, [getTableFieldsInfo, repVers]);

  const reportVersionItems = useMemo(
    () =>
      reportGroupCode ? reportVersions.filter((item) => item.reportGroupCode === reportGroupCode) : reportVersions,
    [reportVersions, reportGroupCode],
  );

  const groupingColumnsItems = useMemo(() => {
    const table = sourceField.split('.')?.[0];
    return table ? groupingColumns.filter((item) => item.table === table) : [];
  }, [groupingColumns, sourceField]);

  const varSourceItems = useMemo(
    () => varSources.map((item) => ({ id: item.varSource, name: `${item.varSource} - ${item.sourceDesc}` })),
    [varSources],
  );

  const aggFuncItems = useMemo(
    () => aggFuncs.map((item) => ({ id: item.aggFunction, name: `${item.aggFunction} - ${item.aggFuncDesc}` })),
    [aggFuncs],
  );

  const onChange = useCallback(() => {
    changeRepVers(undefined);
  }, [changeRepVers]);

  const onChangeVarSource = useCallback(
    (value) => {
      if (value !== 'reg_meta') {
        changeReportGroupCode(undefined);
        changeRepVers(undefined);
        changeRoundingType(undefined);
        changeDecimalQty(undefined);
      }

      if (value === 'rp_package') {
        changeGroupBy(undefined);
        changeAggFunction(undefined);
        changeFilters([]);
      }

      changeSourceField(undefined);
    },
    [
      changeSourceField,
      changeReportGroupCode,
      changeRepVers,
      changeRoundingType,
      changeDecimalQty,
      changeGroupBy,
      changeAggFunction,
      changeFilters,
    ],
  );

  const onChangeSourceField = useCallback(() => {
    changeGroupBy(undefined);
  }, [changeGroupBy]);

  const onChangeRoundingType = useCallback(
    (v) => {
      if (!v) changeDecimalQty(null);
    },
    [changeDecimalQty],
  );

  return (
    <div>
      <div className={styles.row}>
        <SelectField
          items={varSourceItems}
          name="varSource"
          label="Источник выбора данных"
          disabled={!isNew}
          size="small"
          groupStyle={!isEditMode}
          required
          onChange={onChangeVarSource}
        />
        <div />
      </div>

      {varSource === 'reg_meta' && (
        <div className={styles.row}>
          <SelectField
            items={reportGroups}
            name="reportGroupCode"
            label="Код группы отчетных форм"
            disabled={!isNew || filters.length !== 0}
            size="small"
            groupStyle={!isEditMode}
            renderName={renderName}
            allowClear
            onChange={onChange}
          />

          <SelectField
            items={reportVersionItems}
            name="repVers"
            label="Версия отчета"
            disabled={!isNew || filters.length !== 0}
            size="small"
            groupStyle={!isEditMode}
            renderName={renderName}
            allowClear
            required
          />
        </div>
      )}

      <div className={styles.row}>
        <TextField
          name="varCode"
          label="Показатель"
          disabled={!isNew}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={30}
        />
        <TextField
          name="varText"
          label="Описание показателя"
          disabled={!isEditMode}
          size="small"
          groupStyle={!isEditMode}
          required
          maxLength={1000}
        />
      </div>

      <div className={styles.row}>
        <SelectField
          items={varSource === 'rp_package' ? packageSourceFieldItem : sourceFields}
          name="sourceField"
          label="Поле-источник"
          disabled={!isEditMode || filters.length !== 0}
          size="small"
          groupStyle={!isEditMode}
          required
          onChange={onChangeSourceField}
        />
        {varSource !== 'rp_package' && (
          <SelectField
            items={groupingColumnsItems}
            name="groupBy"
            label="Группировка"
            disabled={!isEditMode || !sourceField}
            size="small"
            groupStyle={!isEditMode}
          />
        )}

        {varSource !== 'rp_package' && (
          <SelectField
            items={aggFuncItems}
            name="aggFunction"
            label="Функция агрегации"
            disabled={!isEditMode}
            size="small"
            groupStyle={!isEditMode}
          />
        )}
      </div>

      {varSource === 'reg_meta' && (
        <div className={styles.row}>
          <SelectField
            name="roundingType"
            items={roundTypes}
            label="Тип округления"
            disabled={!isEditMode}
            size="small"
            groupStyle={!isEditMode}
            allowClear
            onChange={onChangeRoundingType}
          />

          <div className={styles.field}>
            <TextField
              name="decimalQty"
              label="Количество разрядов округления"
              disabled={!isEditMode || !roundingType}
              size="small"
              groupStyle={!isEditMode}
              mask={createMask('NUMBER')}
              unmask
              min={-25}
              max={25}
              required={roundingType}
            />

            <div className={styles.tooltip}>
              <Tooltip
                hideOnClick={false}
                content={
                  <div>
                    <p>
                      Если нужно округлить до 0.1, 0.01, 0.001, то нужно выбрать 1, 2, 3 и т.д., а если до 10, 100,
                      1000, то -1, -2, -3 и т.д.
                    </p>
                    <p>Например, при использовании round:</p>
                    <p>(2) 5471.73 -{'>'} 5471.73</p>
                    <p>(1) 5471.73 -{'>'} 5471.70</p>
                    <p>(0) 5471.73 -{'>'} 5472.00</p>
                    <p>(-1) 5471.73 -{'>'} 5470.00</p>
                    <p>(-2) 5471.73 -{'>'} 5500.00</p>
                  </div>
                }
              >
                <span>
                  <Icon name="info" />
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
